import { Badge, BadgeWithDot } from "@/components/base/badges/badges";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import { DataTable, type Column } from "@/components/application/data-table/data-table";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { SlideOver } from "@/components/application/slideout-menus/slide-over";
import { useFioTransactions } from "../../hooks/usePaymentAccountQueries";
import type { FioTransactionRow, PaymentAccountRow } from "../../schemas/paymentAccount";

/**
 * Drill-down panel se staženými FIO transakcemi platebního účtu (UC-007
 * viditelnost výsledku syncu). Otevírá se z karty účtu na /admin/payment-accounts.
 *
 * Princip #1: counter_party_* jsou PII — endpoint je globalAdmin-only + auditovaný.
 * Princip #2: amount_kc = celé jednotky měny, žádný FX přepočet.
 */
export interface FioTransactionsSlideOverProps {
  account: PaymentAccountRow;
  onClose: () => void;
}

function formatDateTime(iso: string): string {
  const ts = Date.parse(iso);
  if (Number.isNaN(ts)) return iso;
  return new Date(ts).toLocaleString("cs-CZ", { dateStyle: "short", timeStyle: "short" });
}

function formatAmount(amountKc: number, currency: string): string {
  // signDisplay always → "+1 500" / "-500"; barvu řeší amountColor.
  return `${new Intl.NumberFormat("cs-CZ", { signDisplay: "always" }).format(amountKc)} ${currency}`;
}

/** Příchozí (+) zeleně, odchozí (−) červeně. */
function amountColor(amountKc: number): string {
  if (amountKc > 0) return "text-green-600 dark:text-green-400";
  if (amountKc < 0) return "text-red-600 dark:text-red-400";
  return "text-tertiary";
}

export function FioTransactionsSlideOver({ account, onClose }: FioTransactionsSlideOverProps) {
  const query = useFioTransactions(account.account_id, true);
  const data = query.data;

  const columns: Column<FioTransactionRow>[] = [
    {
      key: "date",
      header: "Datum",
      className: "whitespace-nowrap",
      cell: (r) => formatDateTime(r.tx_datetime),
      sortValue: (r) => r.tx_datetime,
    },
    {
      key: "vs",
      header: "VS",
      cell: (r) => r.variable_symbol ?? "—",
    },
    {
      key: "counterparty",
      header: "Protistrana",
      cell: (r) => r.counter_party_name ?? "—",
    },
    {
      key: "counterparty_account",
      header: "Účet protistrany",
      cell: (r) => (
        <span className="font-mono text-xs">{r.counter_party_account ?? "—"}</span>
      ),
    },
    {
      key: "amount",
      header: "Částka",
      className: "text-right whitespace-nowrap w-px",
      cell: (r) => (
        <span
          className={`font-mono ${amountColor(r.amount_kc)}`}
          title={r.currency !== "CZK" ? "Celé jednotky měny (haléře/centy zaokrouhleny)" : undefined}
        >
          {formatAmount(r.amount_kc, r.currency)}
        </span>
      ),
      sortValue: (r) => r.amount_kc,
    },
    {
      key: "paired",
      header: "Párování",
      cell: (r) =>
        r.paired ? (
          <BadgeWithDot color="success">
            {r.paired_intent_status ?? "spárováno"}
          </BadgeWithDot>
        ) : (
          <Badge color="gray">nespárováno</Badge>
        ),
    },
    {
      key: "anomaly",
      header: "Anomálie",
      cell: (r) =>
        r.cross_account_anomaly ? (
          <Badge color="warning">cross-account</Badge>
        ) : null,
    },
  ];

  return (
    <SlideOver open size="xwide" title={`Stažené transakce — ${account.label}`} onClose={onClose}>
      <div data-testid="fio-transactions-panel">
        {query.isLoading && <LoadingIndicator label="Načítání…" />}

        {query.isError && (
          <InlineAlert variant="error" title="Chyba načítání">
            <span data-testid="fio-transactions-error">
              Nelze načíst stažené transakce.
            </span>
          </InlineAlert>
        )}

        {data && data.transactions.length === 0 && (
          <InlineAlert variant="info" title="Zatím nic staženo">
            Spusť „Sync teď" a stažené transakce se objeví tady.
          </InlineAlert>
        )}

        {data && data.transactions.length > 0 && (
          <div className="space-y-3">
            {data.truncated && (
              <InlineAlert variant="warning" title="Zkrácený výpis">
                Zobrazeno posledních {data.transactions.length} transakcí.
              </InlineAlert>
            )}
            <DataTable<FioTransactionRow>
              caption={`Stažené FIO transakce účtu ${account.label}`}
              columns={columns}
              rows={data.transactions}
              rowKey={(r) => r.id}
              defaultSort={{ columnKey: "date", direction: "desc" }}
            />
          </div>
        )}
      </div>
    </SlideOver>
  );
}
